import { z } from "zod";
import { idSchema, isoDateSchema, moneySchema, timeSchema, trimmed } from "./common";

export const appointmentStatuses = ["scheduled", "confirmed", "completed", "cancelled", "no_show"] as const;
export const appointmentStatusSchema = z.enum(appointmentStatuses);
export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

export const locationKinds = ["in_person", "remote"] as const;
export const locationKindSchema = z.enum(locationKinds);
export type LocationKind = z.infer<typeof locationKindSchema>;

export const locationInputSchema = z.object({ name: trimmed(60), kind: locationKindSchema.default("in_person") });
export type LocationInput = z.infer<typeof locationInputSchema>;

export const availabilitySlotSchema = z
  .object({ weekday: z.number().int().min(0).max(6), start: timeSchema, end: timeSchema })
  .refine((slot) => slot.start < slot.end, "O encerramento deve ser posterior à abertura");
export const availabilityInputSchema = z
  .object({ slots: z.array(availabilitySlotSchema).max(7) })
  .refine((input) => new Set(input.slots.map((slot) => slot.weekday)).size === input.slots.length, "Cada dia da semana só pode aparecer uma vez");
export type AvailabilitySlot = z.infer<typeof availabilitySlotSchema>;
export type AvailabilityInput = z.infer<typeof availabilityInputSchema>;

export interface Location {
  id: string;
  name: string;
  kind: LocationKind;
  /** weekday: 0 = segunda ... 6 = domingo */
  availability: AvailabilitySlot[];
  inUse: boolean;
}

const newClientSchema = z.object({ name: trimmed(120), phone: z.string().trim().min(8, "Telefone incompleto").max(30) });

export const appointmentInputSchema = z
  .object({
    clientId: idSchema.optional(),
    newClient: newClientSchema.optional(),
    type: trimmed(60),
    locationId: idSchema,
    date: isoDateSchema,
    time: timeSchema,
    durationMinutes: z.number().int().min(10).max(360),
    value: moneySchema,
  })
  .refine((input) => Boolean(input.clientId) !== Boolean(input.newClient), "Informe um cliente existente ou os dados de um novo");
export type AppointmentInput = z.infer<typeof appointmentInputSchema>;

export const appointmentUpdateSchema = z.object({
  type: trimmed(60),
  locationId: idSchema,
  date: isoDateSchema,
  time: timeSchema,
  durationMinutes: z.number().int().min(10).max(360),
  value: moneySchema,
});
export type AppointmentUpdate = z.infer<typeof appointmentUpdateSchema>;

export const appointmentStatusInputSchema = z.object({ status: appointmentStatusSchema });

export const appointmentCheckSchema = z.object({
  appointmentId: idSchema.optional(),
  locationId: idSchema,
  date: isoDateSchema,
  time: timeSchema,
  durationMinutes: z.number().int().min(10).max(360),
});
export type AppointmentCheck = z.infer<typeof appointmentCheckSchema>;

/** Avisos não impedem salvar: o horário é combinado caso a caso. */
export interface AppointmentWarning {
  code: "overlap" | "location_without_availability" | "day_unavailable" | "outside_hours";
  message: string;
}

export interface Appointment {
  id: string;
  clientId: string;
  clientName: string;
  clientPhone: string;
  type: string;
  locationId: string;
  locationName: string;
  locationKind: LocationKind;
  date: string;
  time: string;
  durationMinutes: number;
  value: number;
  status: AppointmentStatus;
}

/** Resumo da agenda de cada cliente, para a lista de clientes. */
export interface ClientScheduleSummary {
  clientId: string;
  lastCompletedDate: string | null;
  nextAppointment: { date: string; time: string } | null;
}

export const appointmentListQuerySchema = z
  .object({ from: isoDateSchema.optional(), to: isoDateSchema.optional(), clientId: idSchema.optional() })
  .refine((query) => Boolean(query.clientId) || Boolean(query.from && query.to), "Informe o período ou o cliente");

/** 0 = segunda ... 6 = domingo. */
export function weekdayOf(date: string): number {
  return (new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7;
}

export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number) as [number, number];
  return hours * 60 + minutes;
};
